/**
 * sealed-vinyl 진입 분기 훅.
 *
 * 진입 시에는 기본적으로 landing 을 노출한다 — 이미 신청한 사용자도 우선 landing 을 보고,
 * CTA(배송비 결제) 클릭 시점에 availability·재고·주소를 재검증해 분기한다.
 * (이미 신청한 활성 주문이 있으면 CTA 단계에서 신청 정보 화면으로 — useSealedVinylCta 참조.)
 *
 * 단, 재고가 소진(isItemOk=false)되면 CTA 가 비활성(소진 화면)이라 CTA-시점 분기를 탈 수
 * 없다. 이때 기존 주문이 있으면 landing/soldOut 대신 detail 로 직진입시켜, 소진 후에도
 * 주문자가 신청 내역·수량 변경·취소에 접근할 수 있게 한다.
 *
 * Resume 분기: 카드 등록(CardRegisterPage) 후 navigate(-1) 로 재진입 시
 * sessionStorage 의 resume.step='payment' 가 있고 + availability·재고 신호 모두 통과면
 * landing 을 건너뛰고 PaymentStep 으로 직진입. consume 한 뒤 즉시 clear.
 */

import { useState } from 'react'

import { clearResume, readResume, type ResumeContext } from '@/pages/SealedVinylPage/lib/resume'

import { ORDER_STATUS } from '../constants'
import { useGetSealedItemStock, useGetSealedOrderAvailability, useGetSealedOrderDetail } from '../query'
import type { GetSealedOrderDetailResponse } from '../types'

type SealedVinylEntryStep = 'landing' | 'payment' | 'soldOut' | 'detail'

interface SealedVinylEntryResult {
  isLoading: boolean
  isError: boolean
  /** detail step 의 UI 데이터 원본. 진입 분기에는 사용하지 않음 */
  activeOrderDetail: GetSealedOrderDetailResponse | null
  /** 진입 시 초기 step */
  entryStep: SealedVinylEntryStep
  /** payment 직진입(resume) 시 복원할 신청 수량 — landing 진입이면 undefined */
  resumeQuantity?: number
  /** NICEPAY 카드 등록 직후 복원 시 자동선택할 카드 id — landing 진입이면 undefined */
  resumeSelectedCardId?: string
}

export const useSealedVinylEntry = (): SealedVinylEntryResult => {
  const detailQuery = useGetSealedOrderDetail()
  const availabilityQuery = useGetSealedOrderAvailability()
  const itemStockQuery = useGetSealedItemStock()

  // mount 시 1회만 sessionStorage 읽고 즉시 clear (재진입 시 중복 영향 방지)
  const [resume] = useState<ResumeContext | null>(() => {
    const ctx = readResume()
    if (ctx) clearResume()
    return ctx
  })

  const isAvailable = availabilityQuery.data ?? false
  const isItemOk = itemStockQuery.data ?? false
  const canOrder = isAvailable && isItemOk
  // detail API 는 사용자의 최근 주문 1건을 반환 (주문은 1회만 가능한 서비스).
  // 결제 완료(COMPLETED) 된 1건만 활성 주문으로 인정 → detail 진입 대상.
  // PAYMENT_PENDING(결제 미완료)·CANCELED(취소) 등은 활성 주문 아님 → soldOut.
  // 404(주문 없음)면 null 로 정규화돼 옴.
  const hasActiveOrder =
    detailQuery.data != null && detailQuery.data.orderInfo.orderStatus === ORDER_STATUS.COMPLETED

  // 진입 분기:
  //  - 카드 등록 후 복귀(resume) + 신청 가능 → PaymentStep 직진입
  //  - 봉투 재고 소진 + 기존 주문 있음 → 신청 내역(detail) 직진입 (소진 후 내역 접근 보장)
  //  - 봉투 재고 소진 + 주문 없음 → 진입 즉시 소진 화면
  //  - 그 외 → landing (이미 신청자도 landing 후 CTA 시점에 detail 로 분기)
  const isPaymentResume = resume?.step === 'payment' && canOrder
  const entryStep: SealedVinylEntryStep = (() => {
    if (isPaymentResume) return 'payment'
    if (!isItemOk && hasActiveOrder) return 'detail'
    if (!isItemOk) return 'soldOut'
    return 'landing'
  })()

  return {
    isLoading: detailQuery.isLoading || availabilityQuery.isLoading || itemStockQuery.isLoading,
    isError: detailQuery.isError || availabilityQuery.isError || itemStockQuery.isError,
    activeOrderDetail: hasActiveOrder ? detailQuery.data : null,
    entryStep,
    resumeQuantity: isPaymentResume ? resume?.quantity : undefined,
    resumeSelectedCardId: isPaymentResume ? resume?.selectedCardId : undefined,
  }
}
